'use client';

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../utils/axios-config';
import { API_ENDPOINTS } from '../config/api-config';

/**
 * Custom hook for searching articles, users, and topics
 * Uses TanStack Query for caching and state management
 */
export function useSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('articles'); // "articles" | "users" | "topics"

  // Build the endpoint based on search type
  const getSearchEndpoint = useCallback((term, type) => {
    switch (type) {
      case 'users':
        return API_ENDPOINTS.SEARCH.USERS(term);
      case 'topics':
        return API_ENDPOINTS.SEARCH.TOPICS(term);
      case 'articles':
      default:
        return API_ENDPOINTS.SEARCH.ARTICLES(term);
    }
  }, []);

  // Search query with debounced search term
  const {
    data: searchResults = [],
    isLoading: isSearching,
    error: searchError,
    refetch,
  } = useQuery({
    queryKey: ['search', searchType, searchTerm],
    queryFn: async () => {
      if (!searchTerm.trim()) {
        return [];
      }
      const endpoint = getSearchEndpoint(searchTerm, searchType);
      const response = await axiosInstance.get(endpoint);
      return response.data?.results || response.data || [];
    },
    enabled: searchTerm.trim().length >= 2, // Only search if at least 2 characters
    staleTime: 30000, // Cache for 30 seconds
    retry: 1,
  });

  // Perform search with optional type
  const performSearch = useCallback(
    (term, type = searchType) => {
      setSearchTerm(term);
      if (type !== searchType) {
        setSearchType(type);
      }
    },
    [searchType]
  );

  // Clear search results
  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return {
    searchTerm,
    searchResults,
    isSearching,
    searchError,
    searchType,
    performSearch,
    setSearchTerm,
    setSearchType,
    clearSearch,
    refetch,
  };
}
